import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { 
  ChevronLeft, User, Mail, Lock, 
  Eye, EyeOff, Check, ShieldCheck, 
  Zap, DollarSign, Star, Smartphone
} from "lucide-react";
import { useAuth } from "../App";
import { authApi } from "../api/services";
import Bokeh from "../components/Bokeh";
import { Toast, useToast, BtnGold, Spinner } from "../components/UI";
import { T, GLOBAL_STYLES } from "../tokens";
import { useTranslation } from "../i18n";

function OtpInput({ value, onChange, onComplete }) {
  const refs = useRef([]);
  const digits = value.split("").concat(Array(6).fill("")).slice(0,6);
  const handleKey = (i,e) => { if(e.key==="Backspace"){const n=[...digits];n[i]="";onChange(n.join(""));if(i>0)refs.current[i-1]?.focus();} };
  const handleChange = (i,e) => { const v=e.target.value.replace(/\D/,"").slice(-1);const n=[...digits];n[i]=v;const j=n.join("");onChange(j);if(v&&i<5)refs.current[i+1]?.focus();if(j.replace(/ /g,"").length===6)onComplete?.(j); };
  const handlePaste = (e) => { const p=e.clipboardData.getData("text").replace(/\D/g,"").slice(0,6);onChange(p.padEnd(6," "));if(p.length===6)onComplete?.(p);refs.current[Math.min(p.length,5)]?.focus();e.preventDefault(); };
  return (
    <div style={{display:"flex",gap:12,justifyContent:"center"}}>
      {digits.map((d,i)=>(
        <input 
          key={i} 
          ref={el=>refs.current[i]=el} 
          type="text" 
          inputMode="numeric" 
          maxLength={1} 
          value={d.trim()} 
          onChange={e=>handleChange(i,e)} 
          onKeyDown={e=>handleKey(i,e)} 
          onPaste={handlePaste}
          onFocus={e=>e.target.style.boxShadow=`0 0 15px ${T.goldBdrS}`}
          onBlur={e=>e.target.style.boxShadow="none"}
          style={{
            width:46,
            height:56,
            borderRadius:12,
            textAlign:"center",
            background:d.trim()?T.goldDim:T.dark4,
            border:`1.5px solid ${d.trim()?T.gold:T.goldBdr}`,
            color:T.goldLt,
            fontSize:22,
            fontWeight:600,
            fontFamily:"inherit",
            outline:"none",
            transition:"all .25s cubic-bezier(0.4, 0, 0.2, 1)",
            boxShadow:d.trim()?`0 0 10px ${T.goldDim}`:"none",
            animation:!d.trim() && value.length === i ? "fyzPulse 2s infinite" : "none"
          }}
        />
      ))}
    </div>
  );
}

let __fyzFieldSeq = 0;
function Field({label,type="text",value,onChange,placeholder,error,icon,rightEl,autoComplete}){
  const [focused,setFocused]=useState(false);
  const [id]=useState(()=>`fyz-f-${++__fyzFieldSeq}`);
  const errId = `${id}-err`;
  return (
    <div style={{marginBottom:14}}>
      <label htmlFor={id} style={{display:"block",fontSize:11,color:T.gold,letterSpacing:".1em",textTransform:"uppercase",marginBottom:5}}>{label}</label>
      <div style={{display:"flex",alignItems:"center",gap:12,background:T.dark4,border:`1.5px solid ${error?T.red:focused?T.gold:T.goldBdr}`,borderRadius:8,padding:"0 14px",height:48,transition:"all .2s"}}>
        {icon&&<span aria-hidden="true" style={{fontSize:15,opacity:.7,flexShrink:0,color:T.goldLt}}>{icon}</span>}
        <input id={id} type={type} value={value} onChange={e=>onChange(e.target.value)} onFocus={()=>setFocused(true)} onBlur={()=>setFocused(false)} placeholder={placeholder} autoComplete={autoComplete} aria-invalid={!!error} aria-describedby={error?errId:undefined} style={{flex:1,background:"transparent",border:"none",outline:"none",color:T.text,fontSize:14,fontFamily:"inherit"}}/>
        {rightEl}
      </div>
      {error&&<div id={errId} role="alert" style={{fontSize:11,color:T.red,marginTop:4,marginLeft:4}}>{error}</div>}
    </div>
  );
}

function PasswordStrength({ password, t }) {
  const requirements = [
    { id: 'length', label: "At least 8 characters", met: password.length >= 8 },
    { id: 'upper',  label: "Includes uppercase", met: /[A-Z]/.test(password) },
    { id: 'number', label: "Includes a number", met: /[0-9]/.test(password) },
    { id: 'symbol', label: "Includes a symbol", met: /[!@#$%^&*(),.?":{}|<>]/.test(password) },
  ];

  const metCount = requirements.filter(r => r.met).length;
  const strength = metCount === 0 ? 'Weak' : metCount <= 2 ? 'Medium' : metCount === 3 ? 'Strong' : 'Very Strong';
  const colors = ['#ff4d4d', '#ffa500', '#3399ff', '#00cc66'];
  const activeColor = metCount > 0 ? colors[metCount - 1] : '#333';

  return (
    <div style={{ marginBottom: 20, animation: 'fyzFadeIn 0.3s ease' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 11, color: T.muted, fontWeight: 500 }}>Password Strength</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
           <div style={{ width: 6, height: 6, borderRadius: '50%', background: activeColor }} />
           <span style={{ fontSize: 11, color: activeColor, fontWeight: 600 }}>{strength}</span>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
        {[1, 2, 3, 4].map(i => (
          <div key={i} style={{ flex: 1, height: 4, borderRadius: 2, background: i <= metCount ? activeColor : T.dark4, transition: 'all 0.3s ease' }} />
        ))}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {requirements.map(req => (
          <div key={req.id} style={{ display: 'flex', alignItems: 'center', gap: 10, opacity: req.met ? 1 : 0.4, transition: 'opacity 0.2s' }}>
            <div style={{ 
              width: 16, height: 16, borderRadius: '50%', 
              background: req.met ? activeColor : 'transparent', 
              border: `1px solid ${req.met ? activeColor : T.goldBdr}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 10, color: req.met ? '#000' : 'transparent'
            }}>
              {req.met && <Check size={10} strokeWidth={3} />}
            </div>
            <span style={{ fontSize: 12, color: req.met ? T.text : T.muted }}>{req.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function LoginRegister() {
  const navigate=useNavigate();const location=useLocation();const {login}=useAuth();
  const { t } = useTranslation();
  const redirect=(()=>{const q=new URLSearchParams(location.search).get("next");if(q&&q.startsWith("/"))return q;return location.state?.from||"/dashboard";})();
  const {msg,visible,show}=useToast();
  const [tab,setTab]=useState("Login");const [method,setMethod]=useState("phone");const [stage,setStage]=useState("form");
  const [phone,setPhone]=useState("");const [email,setEmail]=useState("");const [password,setPassword]=useState("");
  const [confirmPw,setConfirmPw]=useState("");const [name,setName]=useState("");const [otp,setOtp]=useState("");
  const [showPw,setShowPw]=useState(false);const [loading,setLoading]=useState(false);const [errors,setErrors]=useState({});const [resendSec,setResendSec]=useState(0);const [devOtp,setDevOtp]=useState("");
  const timer=useRef(null);
  useEffect(()=>{if(resendSec>0){timer.current=setTimeout(()=>setResendSec(v=>v-1),1000);}return()=>clearTimeout(timer.current);},[resendSec]);

  const validate=()=>{const e={};
    if(method==="phone"){if(!phone||phone.replace(/\D/g,"").length<10)e.phone="Enter a valid 10-digit number";}
    else{
      if(!email||!email.includes("@"))e.email="Enter a valid email";
      if(tab==="Register"){
        if(!password) e.password="Password required";
        else if(password.length<8) e.password="Min 8 characters";
        else if(!/[A-Z]/.test(password)) e.password="At least one uppercase letter";
        else if(!/[0-9]/.test(password)) e.password="At least one number";
        else if(!/[!@#$%^&*(),.?":{}|<>]/.test(password)) e.password="At least one special character";

        if(password!==confirmPw) e.confirmPw="Passwords don't match";
      } else {
        if(!password) e.password="Password required";
      }
    }
    if(tab==="Register"&&!name.trim())e.name="Name required";
    setErrors(e);return Object.keys(e).length===0;};

  const handleSubmit=async()=>{if(!validate())return;setLoading(true);
    try{
      if (method==="phone") {
        const r=await authApi.sendOtp(phone.replace(/\D/g,""), false, undefined, tab.toLowerCase());
        setStage("otp");
        setResendSec(30);
        const dev=r?.data?.dev_otp;setDevOtp(dev||"");show(dev?`OTP (dev): ${dev}`:"OTP sent ✓");
      } else {
        const r=await authApi.sendOtp(email, true, password, tab.toLowerCase());
        setStage("otp");
        setResendSec(30);
        const dev=r?.data?.dev_otp;setDevOtp(dev||"");show(dev?`OTP (dev): ${dev}`:"Please enter the OTP sent to the registered email.✓");
      }
    }catch(err){show(err.message||"Failed to send OTP");if(err.errors){const m={};Object.keys(err.errors).forEach(k=>{m[k]=err.errors[k][0];});setErrors(m);}}
    finally{setLoading(false);}};

  const handleOtp=async(code)=>{if(code.length<6)return;setLoading(true);
    try{
      let res;
      if (method==="phone") {
        res=await authApi.verifyOtp(phone.replace(/\D/g,""),code,name||undefined);
      } else {
        res=tab==="Register"?await authApi.register({name,email,password,password_confirmation:confirmPw,otp:code}):await authApi.login(email,password,code);
      }
      login(res.data.user,res.data.token);setStage("success");show("Verified! ✓");setTimeout(()=>navigate(redirect),1000);
    }catch(err){show(err.message||"Invalid OTP");setOtp("");if(err.errors){const m={};Object.keys(err.errors).forEach(k=>{m[k]=err.errors[k][0];});setErrors(m);setStage("form");}}finally{setLoading(false);}};

  const resendOtp=async()=>{if(resendSec>0)return;setLoading(true);try{const r=await authApi.sendOtp(method==="phone"?phone.replace(/\D/g,""):email, method==="email", method==="email"?password:undefined, tab.toLowerCase());setResendSec(30);const dev=r?.data?.dev_otp;setDevOtp(dev||"");show(dev?`OTP (dev): ${dev}`:"OTP resent ✓");}catch{show("Failed to resend.");}finally{setLoading(false);}};
  const switchTab=(t)=>{setTab(t);setStage("form");setErrors({});setPhone("");setEmail("");setPassword("");setName("");setOtp("");setDevOtp("");};
  const handleForgotPassword=async()=>{if(!email||!email.includes("@")){setErrors({...errors,email:"Please enter your email first"});return;}setLoading(true);try{await authApi.forgotPassword(email);show(t("auth.reset_sent"));}catch(err){show(err.message||"Failed to send reset link");}finally{setLoading(false);}};
  const handleSocial=(provider)=>{show(`${provider.charAt(0).toUpperCase() + provider.slice(1)} Login coming soon!`);};

  return (<>
    <style>{GLOBAL_STYLES}</style>
    <div style={{height:"calc(100vh - 76px)",overflow:"hidden",display:"flex",position:"relative",fontFamily:"'DM Sans',sans-serif",background:`radial-gradient(ellipse at 60% 0%,#2A1F08 0%,#0D0B08 60%)`}}>
      <Bokeh count={24} style={{position:"fixed",inset:0,zIndex:0}}/>
      <div style={{flex:1,position:"relative",overflow:"hidden",display:"flex",flexDirection:"column",justifyContent:"center",padding:"60px 56px",background:"linear-gradient(160deg,#1A1208 0%,#0D0B08 100%)",borderRight:`1px solid ${T.goldBdr}`}}>
        <Bokeh count={16}/>
        <div onClick={()=>navigate("/")} style={{fontFamily:"Cormorant Garamond,serif",fontSize:42,color:T.gold,fontStyle:"italic",marginBottom:40,cursor:"pointer",position:"relative",zIndex:1}}>Fayriza</div>
        <div style={{position:"relative",zIndex:1,maxWidth:400}}>
          <h2 style={{fontFamily:"Cormorant Garamond,serif",fontSize:"clamp(30px,3.5vw,44px)",fontWeight:300,color:T.text,lineHeight:1.15,marginBottom:18}}>
            India's Most<br/><em style={{color:T.goldLt,fontWeight:600,fontStyle:"italic"}}>Premium</em> Salon<br/>Experience
          </h2>
          <p style={{fontSize:14.5,color:T.muted,lineHeight:1.8,marginBottom:28}}>Book luxury beauty services at 500+ top salons. Join 50,000+ happy clients on Fayriza.</p>
          {[
            {icon:<ShieldCheck size={18} />, text:"Verified Premium Salons"},
            {icon:<Zap size={18} />, text:"Instant Online Booking"},
            {icon:<DollarSign size={18} />, text:"20% Off with Gold Membership"},
            {icon:<Star size={18} />, text:"Earn Loyalty Points Every Visit"}
          ].map(p=>(
            <div key={p.text} style={{display:"flex",alignItems:"center",gap:12,marginBottom:14}}>
              <div style={{width:36,height:36,borderRadius:9,background:T.goldDim,border:`1px solid ${T.goldBdrS}`,display:"flex",alignItems:"center",justifyContent:"center",color:T.goldLt,flexShrink:0}}>{p.icon}</div>
              <span style={{fontSize:14,color:T.text}}>{p.text}</span>
            </div>
          ))}
        </div>
      </div>
      <div style={{width:480,height:"100%",flexShrink:0,display:"flex",flexDirection:"column",justifyContent:"center",alignItems:"center",padding:"32px 48px",position:"relative",zIndex:1,overflowY:"auto"}}>
        {stage==="otp" && (
          <div style={{position:"absolute",top:32,left:48}}>
            <button onClick={()=>setStage("form")} style={{background:"none",border:"none",color:T.muted,cursor:"pointer",fontFamily:"inherit",fontSize:13.5,display:"flex",alignItems:"center",gap:6,padding:"8px 0"}} onMouseOver={e=>e.currentTarget.style.color=T.goldLt} onMouseOut={e=>e.currentTarget.style.color=T.muted}>
              <ChevronLeft size={16} /> {t("common.back")}
            </button>
          </div>
        )}
        {stage==="success"?(
          <div style={{textAlign:"center",animation:"fyzFadeUp .5s ease"}}>
            <div style={{width:80,height:80,borderRadius:"50%",background:T.goldDim,border:`2px solid ${T.gold}`,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 24px",color:T.goldLt}}>
              <Check size={40} strokeWidth={3} />
            </div>
            <div style={{fontFamily:"Cormorant Garamond,serif",fontSize:28,color:T.text,marginBottom:8}}>Welcome to Fayriza!</div>
            <p style={{fontSize:14,color:T.muted,marginBottom:24}}>Redirecting you now...</p><Spinner size={32}/>
          </div>
        ):stage==="otp"?(
          <div style={{width:"100%",animation:"fyzFadeUp .4s ease",textAlign:"center",position:"relative"}}>
            <div style={{marginTop:0,marginBottom:32}}>
              <div style={{width:64,height:64,borderRadius:"50%",background:T.goldDim,border:`1px solid ${T.goldBdr}`,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 24px",boxShadow:`0 0 20px ${T.goldDim}`,color:T.goldLt}}>
                <Lock size={24} strokeWidth={1.5} />
              </div>
              <div style={{fontFamily:"Cormorant Garamond,serif",fontSize:32,color:T.text,marginBottom:8,letterSpacing:"1px"}}>{t("auth.verify_otp")}</div>
              <div style={{fontSize:13.5,color:T.muted,lineHeight:1.6}}>
                {t("auth.otp_placeholder")}
                <div style={{color:T.goldLt,fontWeight:600,marginTop:4,fontSize:15,letterSpacing:"0.5px"}}>{method === "phone" ? `+91 ${phone}` : email}</div>
              </div>
            </div>

            <div style={{marginBottom:32}}>
              <OtpInput value={otp} onChange={setOtp} onComplete={handleOtp}/>
            </div>

            {devOtp && (
              <div role="alert" style={{
                background:"rgba(255,193,7,0.08)",
                border:"1px solid rgba(255,193,7,0.5)",
                borderRadius:10,
                padding:"14px 16px",
                marginBottom:24,
                textAlign:"left",
                color:"#ffd860"
              }}>
                <div style={{fontSize:13,fontWeight:600,marginBottom:8,display:"flex",alignItems:"center",gap:8}}>
                  <span aria-hidden="true">🛠️</span>
                  <span>Dev mode — email not sent</span>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8,flexWrap:"wrap"}}>
                  <code
                    onClick={()=>{try{navigator.clipboard?.writeText(devOtp);show("Copied ✓");}catch{}}}
                    title="Click to copy"
                    style={{
                      background:"rgba(255,193,7,0.15)",
                      border:"1px solid rgba(255,193,7,0.4)",
                      borderRadius:6,
                      padding:"6px 12px",
                      fontFamily:"monospace",
                      fontSize:18,
                      letterSpacing:"4px",
                      fontWeight:700,
                      color:"#ffe082",
                      cursor:"pointer",
                      userSelect:"all"
                    }}
                  >{devOtp}</code>
                  <button
                    type="button"
                    onClick={()=>{setOtp(devOtp);handleOtp(devOtp);}}
                    style={{
                      background:"rgba(255,193,7,0.2)",
                      border:"1px solid rgba(255,193,7,0.5)",
                      color:"#ffe082",
                      borderRadius:6,
                      padding:"6px 12px",
                      fontSize:12,
                      fontWeight:600,
                      cursor:"pointer",
                      fontFamily:"inherit"
                    }}
                  >Use this code</button>
                </div>
                <div style={{fontSize:11,opacity:0.7,lineHeight:1.5}}>
                  Stage and production environments still email this code normally.
                </div>
              </div>
            )}

            {loading && (
              <div style={{display:"flex",justifyContent:"center",marginBottom:24}}>
                <Spinner size={30}/>
              </div>
            )}

            <div style={{marginTop:8}}>
              <span 
                onClick={resendOtp} 
                style={{
                  color:resendSec>0?T.subtle:T.goldLt,
                  cursor:resendSec>0?"default":"pointer",
                  fontSize:13.5,
                  fontWeight:600,
                  transition:"all .2s",
                  borderBottom:resendSec>0?"none":`1px solid ${T.goldBdr}`,
                  paddingBottom:2
                }}
              >
                {resendSec>0 ? t("auth.resend_in",{s:resendSec}) : t("auth.resend_otp")}
              </span>
            </div>
          </div>
        ) : (
          <div style={{width:"100%",animation:"fyzFadeUp .4s ease"}}>
            <div onClick={()=>navigate("/")} style={{fontFamily:"Cormorant Garamond,serif",fontSize:28,color:T.gold,fontStyle:"italic",textAlign:"center",marginBottom:16,cursor:"pointer"}}>Fayriza</div>
            <div style={{display:"flex",background:"rgba(0,0,0,0.4)",border:`1px solid ${T.goldBdr}`,borderRadius:30,padding:5,marginBottom:24,boxShadow:"inset 0 2px 8px rgba(0,0,0,0.5)"}}>
              {[["Login",t("nav.login")],["Register",t("auth.register_cta")]].map(([id,lbl])=>(
                <div key={id} onClick={()=>switchTab(id)} style={{flex:1,textAlign:"center",padding:"10px 0",borderRadius:26,background:tab===id?"linear-gradient(135deg, #D4AF37 0%, #AA7C11 100%)":"transparent",color:tab===id?"#1A1208":T.muted,fontSize:14.5,fontWeight:600,cursor:"pointer",transition:"all .3s cubic-bezier(0.4, 0, 0.2, 1)",boxShadow:tab===id?`0 4px 14px rgba(212,175,55,0.4)`:"none",textShadow:tab===id?"none":"0 1px 2px rgba(0,0,0,0.8)",letterSpacing:"0.5px"}}>
                  {lbl}
                </div>
              ))}
            </div>
            {tab==="Register"&&<Field label={t("auth.name")} value={name} onChange={setName} placeholder={t("auth.name_placeholder")} icon={<User size={16} />} error={errors.name}/>}
            {method==="phone"?(
              <div style={{marginBottom:14}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:5}}>
                  <label style={{fontSize:11,color:T.gold,letterSpacing:".1em",textTransform:"uppercase"}}>{t("auth.phone")}</label>
                  <span onClick={()=>{setMethod("email");setErrors({});}} style={{fontSize:11.5,color:T.muted,cursor:"pointer",transition:"color .2s"}} onMouseOver={e=>e.target.style.color=T.goldLt} onMouseOut={e=>e.target.style.color=T.muted}>Use Email Instead</span>
                </div>
                <div style={{display:"flex",alignItems:"center",background:T.dark4,border:`1.5px solid ${errors.phone?T.red:T.goldBdr}`,borderRadius:8,overflow:"hidden",height:48}}>
                  <div style={{padding:"0 16px",borderRight:`1px solid ${T.goldBdr}`,fontSize:13.5,color:T.muted,height:"100%",display:"flex",alignItems:"center",gap:8,flexShrink:0}}><Smartphone size={16} /> +91</div>
                  <input type="tel" value={phone} onChange={e=>setPhone(e.target.value.replace(/\D/g,"").slice(0,10))} placeholder={t("auth.phone_placeholder")} style={{flex:1,background:"transparent",border:"none",outline:"none",color:T.text,fontSize:14,fontFamily:"inherit",padding:"0 14px"}}/>
                </div>
                {errors.phone&&<div style={{fontSize:11,color:T.red,marginTop:4,marginLeft:4}}>{errors.phone}</div>}
              </div>
            ):(
              <>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:-14,position:"relative",zIndex:2}}>
                   <span />
                   <span onClick={()=>{setMethod("phone");setErrors({});}} style={{fontSize:11.5,color:T.muted,cursor:"pointer",transition:"color .2s"}} onMouseOver={e=>e.target.style.color=T.goldLt} onMouseOut={e=>e.target.style.color=T.muted}>Use Phone Instead</span>
                </div>
                <Field label={t("auth.email")} type="email" value={email} onChange={setEmail} placeholder={t("auth.email_placeholder")} icon={<Mail size={16} />} error={errors.email}/>
                <Field label={t("auth.password")}
                  type={showPw?"text":"password"}
                  value={password}
                  onChange={setPassword}
                  placeholder={t("auth.password_placeholder")}
                  icon={<Lock size={16} />}
                  error={errors.password}
                  rightEl={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <button type="button" onClick={()=>setShowPw(v=>!v)} style={{background:"none", border:"none", color:T.subtle,cursor:"pointer",display:"flex", alignItems:"center", padding:4}}>{showPw ? <EyeOff size={18} /> : <Eye size={18} />}</button>
                      {password.length > 0 && tab === "Register" && (
                        <div style={{
                          width: 18, height: 18, borderRadius: '50%',
                          background: (password.length >= 8 && /[A-Z]/.test(password) && /[0-9]/.test(password) && /[!@#$%^&*(),.?":{}|<>]/.test(password)) ? '#00cc66' : T.dark3,
                          display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#000'
                        }}><Check size={10} strokeWidth={3} /></div>
                      )}
                    </div>
                  }
                />
                {tab === "Register" && password.length > 0 && <PasswordStrength password={password} t={t} />}
                {tab==="Register"&&<Field label={t("auth.confirm_password")} type="password" value={confirmPw} onChange={setConfirmPw} placeholder={t("auth.password_placeholder")} icon={<Lock size={16} />} error={errors.confirmPw}/>}
              </>
            )}
            <div style={{marginTop: 20}}>
              <BtnGold full onClick={handleSubmit} disabled={loading} style={{padding:"14px 16px"}}>
                {loading?<Spinner size={20}/>:tab==="Login"?(method==="phone"?t("auth.send_otp")+" →":t("auth.login_cta")+" →"):(method==="phone"?t("auth.send_otp")+" →":t("auth.register_cta")+" →")}
              </BtnGold>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:12,margin:"20px 0"}}><div style={{flex:1,height:1,background:T.goldBdr}}/><span style={{fontSize:12,color:T.subtle,textTransform:"uppercase",letterSpacing:"0.05em"}}>{t("common.or")}</span><div style={{flex:1,height:1,background:T.goldBdr}}/></div>
            <div style={{display:"flex",gap:16}}>
              <button onClick={()=>handleSocial("google")} style={{flex:1,padding:"10px",background:T.dark4,border:`1px solid ${T.goldBdr}`,borderRadius:8,color:T.goldLt,fontSize:13,fontFamily:"inherit",fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:10,transition:"all .2s"}}>
                <svg width="18" height="18" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.7 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/><path fill="none" d="M0 0h48v48H0z"/></svg>
                Google
              </button>
              <button onClick={()=>handleSocial("facebook")} style={{flex:1,padding:"10px",background:T.dark4,border:`1px solid ${T.goldBdr}`,borderRadius:8,color:T.goldLt,fontSize:13,fontFamily:"inherit",fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:10,transition:"all .2s"}}>
                <svg width="18" height="18" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#1877F2"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.469h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.469h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                Facebook
              </button>
            </div>
            {tab==="Login"&&method==="email"&&(
              <p style={{textAlign:"center",marginTop:24,fontSize:13,color:T.muted}}>
                <span onClick={handleForgotPassword} style={{color:T.goldLt,cursor:"pointer",fontWeight:500,textDecoration:"underline",textUnderlineOffset:4}}>{t("auth.forgot_password")}</span>
              </p>
            )}
          </div>
        )}
      </div>
    </div>
    <Toast msg={msg} visible={visible}/>
  </>);
}

